import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { CalendarIcon, UsersIcon, CurrencyDollarIcon, TrophyIcon, ArrowLeftIcon, TrashIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolsApi from '../services/poolsApi'
import { isPoolAdmin } from '../utils/roles'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  description: string
  type: string
  status: 'active' | 'completed' | 'cancelled'
  startDate: string
  endDate?: string
  currentParticipants: number
  maxParticipants: number
  entryFee: number
  pickDeadline?: string
  createdAt: string
  createdBy: string
  games?: Game[]
  participants?: Participant[]
}

interface Game {
  gameId: string
  homeTeam: string
  awayTeam: string
  homeConference: string
  awayConference: string
  startDate: string
  week: number
  venue: string
  lines: Line[]
  mediaOutlets: string[]
  homeTeamRankings?: {
    ap?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
    coaches?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
  }
  awayTeamRankings?: {
    ap?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
    coaches?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
  }
}

interface Line {
  provider: string
  spread: number
  overUnder?: number
}

interface Participant {
  userId: string
  name: string
  email: string
  joinedAt: string
  paid?: boolean
  picks?: Pick[]
}

interface Pick {
  gameId: string
  selectedTeam: string
  confidence?: number
}

export default function PoolDetailPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const navigate = useNavigate()
  const auth0 = useAuth0()
  const { user, getAccessTokenSilently } = auth0
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [updatingPayment, setUpdatingPayment] = useState<string | null>(null)

  const userIsPoolAdmin = isPoolAdmin(user)
  const poolsApi = new PoolsApi(auth0)

  useEffect(() => {
    if (poolId) {
      fetchPool()
    }
  }, [poolId])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      
      // Check if this is a horse racing pool and redirect to correct route
      if (data.pool?.type === 'horse-racing') {
        navigate(`/admin/pools/horse-racing/${poolId}`)
        return
      }
      
      setPool(data.pool)
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinPool = async () => {
    if (!poolId) return
    
    try {
      setJoining(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/participants`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to join pool: ${response.statusText}`)
      }

      // Refresh pool data to show updated participant count
      await fetchPool()
    } catch (err) {
      console.error('Error joining pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to join pool')
    } finally {
      setJoining(false)
    }
  }

  const handleDeletePool = async () => {
    if (!poolId || !pool) return
    
    try {
      setDeleting(true)
      await poolsApi.deletePool(poolId)
      
      // Navigate back to pools list
      navigate('/admin/pools/football')
    } catch (err) {
      console.error('Error deleting pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete pool')
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
      setDeleteConfirmation('')
    }
  }

  const openDeleteModal = () => {
    setShowDeleteModal(true)
    setDeleteConfirmation('')
    setError(null)
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false)
    setDeleteConfirmation('')
  }

  const handleMarkPaid = async (participant: Participant, paid: boolean) => {
    if (!poolId || updatingPayment) return

    try {
      setUpdatingPayment(participant.userId)
      await poolsApi.markParticipantPaid(poolId, participant.userId, paid)
      
      // Refresh pool data
      await fetchPool()
    } catch (err) {
      console.error('Error updating payment status:', err)
      setError(err instanceof Error ? err.message : 'Failed to update payment status')
    } finally {
      setUpdatingPayment(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatGameTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const isUserParticipant = pool?.participants?.some(p => p.userId === user?.sub)

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pool not found</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link 
            to="/admin/pools/football" 
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{pool.name}</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{pool.description}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pool.status)}`}>
            {pool.status.charAt(0).toUpperCase() + pool.status.slice(1)}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Created {formatDate(pool.createdAt)}
          </span>
        </div>
      </div>

      <div className="space-y-8">
        {/* Pool Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pool Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg mx-auto mb-2">
                <UsersIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {pool.currentParticipants}/{pool.maxParticipants}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Participants</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg mx-auto mb-2">
                <CurrencyDollarIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">${pool.entryFee}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Entry Fee</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg mx-auto mb-2">
                <TrophyIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${pool.entryFee * pool.currentParticipants}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Prize Pool</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg mx-auto mb-2">
                <CalendarIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {pool.games?.length || 0}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Games</div>
            </div>
          </div>
        </div>

        {/* Pick Deadline Info */}
        {pool.pickDeadline && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
              <div>
                <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Pick Deadline
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  {formatDate(pool.pickDeadline)} at {new Date(pool.pickDeadline).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {new Date() > new Date(pool.pickDeadline) && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                    ⚠️ Deadline has passed - picks are locked
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile-first layout: Actions and Participants before Games */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Actions and Participants - Show first on mobile, sidebar on desktop */}
          <div className="lg:order-2 space-y-6">
            {/* Join Pool / Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h3>
              
              {!isUserParticipant ? (
                <button
                  onClick={handleJoinPool}
                  disabled={joining || pool.currentParticipants >= pool.maxParticipants || pool.status !== 'active'}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {joining ? 'Joining...' : 'Join Pool'}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-green-800 dark:text-green-200 font-medium">
                      You're in this pool!
                    </div>
                  </div>
                  <Link
                    to={`/pools/${poolId}/picks`}
                    className="w-full block text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Make Picks
                  </Link>
                  <Link
                    to={`/pools/${poolId}/my-picks`}
                    className="w-full block text-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    View My Picks
                  </Link>
                  <Link
                    to={`/pools/${poolId}/standings`}
                    className="w-full block text-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    View Standings
                  </Link>
                </div>
              )}
              
              {pool.currentParticipants >= pool.maxParticipants && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">
                  Pool is full
                </p>
              )}

              {/* Admin Actions */}
              {userIsPoolAdmin && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-2">
                  <Link
                    to={`/admin/pools/football/${poolId}/results`}
                    className="w-full block text-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Set Game Results
                  </Link>
                  <Link
                    to={`/admin/pools/football/${poolId}/edit`}
                    className="w-full block text-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    Edit Pool
                  </Link>
                  <button
                    onClick={openDeleteModal}
                    className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Delete Pool
                  </button>
                </div>
              )}
            </div>

            {/* Participants */}
            {pool.participants && pool.participants.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Participants ({pool.participants.length})
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {pool.participants.map((participant) => (
                    <div key={participant.userId} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {participant.name || participant.email || 'Unknown User'}
                          </div>
                          {/* Payment Status Badge */}
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            participant.paid 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {participant.paid ? (
                              <>
                                <CheckCircleIcon className="w-3 h-3 mr-1" />
                                Paid
                              </>
                            ) : (
                              <>
                                <XCircleIcon className="w-3 h-3 mr-1" />
                                Unpaid
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Joined {new Date(participant.joinedAt).toLocaleDateString()}
                          {participant.picks && participant.picks.length > 0 && (
                            <span className="ml-2 text-green-600 dark:text-green-400">
                              • {participant.picks.length} picks made
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Admin Payment Controls */}
                      {userIsPoolAdmin && (
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleMarkPaid(participant, !participant.paid)}
                            disabled={updatingPayment === participant.userId}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              participant.paid
                                ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800'
                                : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {updatingPayment === participant.userId ? (
                              'Updating...'
                            ) : participant.paid ? (
                              'Mark Unpaid'
                            ) : (
                              'Mark Paid'
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Games - Show after actions/participants on mobile, main content on desktop */}
          <div className="lg:order-1 lg:col-span-2">
            {pool.games && pool.games.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Games</h2>
                <div className="space-y-4">
                  {pool.games.map((game) => (
                    <div key={game.gameId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-lg font-medium text-gray-900 dark:text-white">
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                {game.awayTeamRankings?.ap?.rank && (
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                                    {game.awayTeamRankings.ap.rank}
                                  </span>
                                )}
                                <span>
                                  {game.awayTeam}
                                  {game.awayTeamRecord && (
                                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                                      ({game.awayTeamRecord.wins}-{game.awayTeamRecord.losses})
                                    </span>
                                  )}
                                </span>
                              </div>
                              <span>@</span>
                              <div className="flex items-center">
                                {game.homeTeamRankings?.ap?.rank && (
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                                    {game.homeTeamRankings.ap.rank}
                                  </span>
                                )}
                                <span>
                                  {game.homeTeam}
                                  {game.homeTeamRecord && (
                                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                                      ({game.homeTeamRecord.wins}-{game.homeTeamRecord.losses})
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {formatGameTime(game.startDate)} • {game.venue}
                          </div>
                          {game.lines.length > 0 && (
                            <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              {game.lines.map((line, index) => (
                                <span key={index} className="mr-4">
                                  {line.provider}: {line.spread > 0 ? '+' : ''}{line.spread}
                                  {line.overUnder && ` O/U ${line.overUnder}`}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {game.mediaOutlets.length > 0 && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {game.mediaOutlets.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mt-4">
                Delete Pool
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone. This will permanently delete the pool "{pool?.name}", 
                  all games, participant data, and picks.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Type "<span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">DELETE</span>" to confirm:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="Type DELETE here"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={closeDeleteModal}
                    className="flex-1 px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeletePool}
                    disabled={deleteConfirmation !== 'DELETE' || deleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Delete Pool'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
